import { EyeIcon, EyeOffIcon } from '@heroicons/react/outline'
import classNames from 'classnames'
import { useField } from 'formik'
import { useRef, useState } from 'react'

import { Button } from '@/components'

import { IconAlert } from '../../Icons'

type Props = {
  addOnBefore?: string
  label?: string
  labelHelper?: string
  name: string
  placeholder?: string
  type?: 'text' | 'email' | 'password' | 'hidden'
  isTitle?: boolean
  className?: string
  autoFocus?: boolean
  onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  maxLength?: number
  bordered?: boolean
  value?: string
}

const Input: React.FC<React.PropsWithChildren<Props>> = ({
  addOnBefore,
  label,
  labelHelper,
  isTitle = false,
  className,
  bordered,
  ...props
}) => {
  const [field, { touched, error }] = useField(props)
  const [showPassword, setShowPassword] = useState(false)
  const ref = useRef<HTMLInputElement>(null)
  const hasError = touched && error && typeof error === 'string'

  const isPassword = props.type === 'password'
  const classes = {
    input: classNames(
      'py-3 px-4 w-full rounded-md outline-none font-sans capsize focus:ring focus:ring-purple-extra-light',
      {
        'border border-grey-light focus:border-purple-base': bordered,
        'pl-10': addOnBefore,
        'border-red-base': hasError && !isTitle,
        'text-2xl font-medium px-0 focus:ring-0': isTitle,
      },
      className,
    ),
    togglePassword: classNames('icon-sm'),
    addOn: classNames('absolute z-50 text-navy-dark px-4 p-3', {
      'border-red-base': hasError,
    }),
  }

  return (
    <div className="relative text-left">
      <label htmlFor={props.name}>
        {label ? (
          <div className="mb-2 font-medium text-navy-dark">
            {label} {labelHelper ? <span className="font-normal text-grey-base">{labelHelper}</span> : null}
          </div>
        ) : null}

        <div className="relative">
          {addOnBefore ? <div className={classes.addOn}>{addOnBefore}</div> : null}

          <input
            id={props.name}
            ref={ref}
            className={classes.input}
            {...field}
            {...props}
            {...(isPassword && showPassword ? { type: 'text' } : null)}
          />

          {isPassword ? (
            <div className="absolute right-3 top-0 z-50 flex h-full items-center justify-center text-grey-base text-sm leading-none">
              <Button type="button" onClick={() => setShowPassword(!showPassword)} variant="inline">
                {showPassword ? (
                  <EyeOffIcon className={classes.togglePassword} />
                ) : (
                  <EyeIcon className={classes.togglePassword} />
                )}
              </Button>
            </div>
          ) : null}

          {props.maxLength ? (
            <div className="mt-2 text-right text-grey-base text-xs">
              {ref?.current?.value?.length ?? 0}/{props.maxLength}
            </div>
          ) : null}
        </div>
      </label>

      {hasError ? (
        <div
          className={classNames('flex items-center text-red-base text-sm', {
            'mt-1.5': !isTitle,
          })}
        >
          <IconAlert className="fill-current icon-xs mr-1 inline-block text-red-base" />
          {error}
        </div>
      ) : null}
    </div>
  )
}

export default Input
